import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  Shield, AlertTriangle, Users, DollarSign, Trophy, Gamepad2,
  Eye, CheckCircle, XCircle, RefreshCw, Search, Settings,
  TrendingUp, Clock, Ban, UserCheck, ChevronRight, Loader2,
  Download, Trash2, PauseCircle, Play, Wallet, Flag, FileText, Coins, Target,
  CreditCard, Square, CheckSquare, AlertCircle, UserX, UserPlus, X, Megaphone
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import DashboardLayout from '../components/DashboardLayout';
import { useAlert, useConfirm, usePrompt } from '../components/CustomDialog';
import AdminDeposits from '../components/AdminDeposits';
import AdminUsers from '../components/AdminUsers';
import CompliancePanel from '../components/CompliancePanel';
import AdminAnnouncements from '../components/AdminAnnouncements';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminPanel = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { showAlert, AlertDialog } = useAlert();
  const { showConfirm, ConfirmDialog } = useConfirm();
  const { showPrompt, PromptDialog } = usePrompt();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [stats, setStats] = useState(null);
  const [financials, setFinancials] = useState(null);
  const [users, setUsers] = useState([]);
  const [matches, setMatches] = useState([]);
  const [disputes, setDisputes] = useState([]);
  const [withdrawals, setWithdrawals] = useState([]);
  const [pendingDepositsCount, setPendingDepositsCount] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedItem, setSelectedItem] = useState(null);
  const [resolutionNote, setResolutionNote] = useState('');
  const [processing, setProcessing] = useState(false);
  const [matchStatusFilter, setMatchStatusFilter] = useState('');
  
  // New Users & Depositors Analytics State
  const [userAnalytics, setUserAnalytics] = useState(null);
  const [depositorAnalytics, setDepositorAnalytics] = useState(null);
  const [showNewUsersModal, setShowNewUsersModal] = useState(false);
  const [showDepositorsModal, setShowDepositorsModal] = useState(false);
  const [recentUsers, setRecentUsers] = useState([]);
  const [recentDepositors, setRecentDepositors] = useState([]);
  const [loadingModalData, setLoadingModalData] = useState(false);

  useEffect(() => {
    loadAdminData();
  }, []);

  const loadAdminData = async () => {
    setLoading(true);
    try {
      const [statsRes, financialsRes, usersRes, disputesRes, withdrawalsRes, depositsRes, userAnalyticsRes, depositorAnalyticsRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { withCredentials: true }).catch(err => {
          console.error('Stats API error:', err);
          return { data: null };
        }),
        axios.get(`${API}/admin/financial-dashboard`, { withCredentials: true }).catch(err => {
          console.error('Financials API error:', err);
          return { data: null };
        }),
        axios.get(`${API}/admin/users`, { withCredentials: true }).catch(err => {
          console.error('Users API error:', err);
          return { data: [] };
        }),
        axios.get(`${API}/admin/disputes`, { withCredentials: true }).catch(err => {
          console.error('Disputes API error:', err);
          return { data: [] };
        }),
        axios.get(`${API}/admin/withdrawals`, { withCredentials: true }).catch(err => {
          console.error('Withdrawals API error:', err);
          return { data: [] };
        }),
        axios.get(`${API}/admin/deposits`, { withCredentials: true }).catch(err => {
          console.error('Deposits API error:', err);
          return { data: { deposits: [] } };
        }),
        axios.get(`${API}/admin/user-analytics`, { withCredentials: true }).catch(err => {
          console.error('User Analytics API error:', err);
          return { data: null };
        }),
        axios.get(`${API}/admin/depositor-analytics`, { withCredentials: true }).catch(err => {
          console.error('Depositor Analytics API error:', err);
          return { data: null };
        })
      ]);
      
      console.log('Stats data:', statsRes.data);
      console.log('Financials data:', financialsRes.data);
      console.log('Withdrawals data:', withdrawalsRes.data);
      console.log('Deposits data:', depositsRes.data);
      
      setStats(statsRes.data);
      setFinancials(financialsRes.data);
      setUsers(usersRes.data || []);
      setDisputes(disputesRes.data || []);
      setWithdrawals(withdrawalsRes.data || []);
      setUserAnalytics(userAnalyticsRes.data);
      setDepositorAnalytics(depositorAnalyticsRes.data);
      const depositsData = depositsRes.data?.deposits || [];
      setPendingDepositsCount(depositsData.filter(d => d.status === 'under_review' || d.status === 'pending').length);
    } catch (error) {
      console.error('Admin load error:', error);
      toast.error('Failed to load admin data');
    } finally {
      setLoading(false);
    }
  };

  // Load recent users for modal
  const loadRecentUsers = async () => {
    setLoadingModalData(true);
    try {
      const res = await axios.get(`${API}/admin/user-analytics/recent?limit=20`, { withCredentials: true });
      setRecentUsers(res.data?.users || []);
      setShowNewUsersModal(true);
    } catch (error) {
      console.error('Failed to load recent users:', error);
      toast.error('Failed to load recent users');
    } finally {
      setLoadingModalData(false);
    }
  };

  // Load recent depositors for modal
  const loadRecentDepositors = async () => {
    setLoadingModalData(true);
    try {
      const res = await axios.get(`${API}/admin/depositor-analytics/recent?limit=20`, { withCredentials: true });
      setRecentDepositors(res.data?.depositors || []);
      setShowDepositorsModal(true);
    } catch (error) {
      console.error('Failed to load recent depositors:', error);
      toast.error('Failed to load recent depositors');
    } finally {
      setLoadingModalData(false);
    }
  };

  const loadMatches = async (status = '') => {
    try {
      const url = status 
        ? `${API}/admin/matches/all?status=${status}`
        : `${API}/admin/matches/all`;
      const res = await axios.get(url, { withCredentials: true });
      setMatches(res.data || []);
    } catch (error) {
      toast.error('Failed to load matches');
    }
  };

  const searchMatches = async () => {
    if (!searchQuery.trim()) {
      loadMatches(matchStatusFilter);
      return;
    }
    try {
      const res = await axios.get(`${API}/admin/search-matches?q=${encodeURIComponent(searchQuery)}`, { withCredentials: true });
      setMatches(res.data || []);
      setActiveTab('matches');
    } catch (error) {
      toast.error('Search failed');
    }
  };

  useEffect(() => {
    if (activeTab === 'matches') {
      loadMatches(matchStatusFilter);
    }
  }, [activeTab, matchStatusFilter]);

  const resolveDispute = async (matchId, winnerId) => {
    if (!resolutionNote.trim()) {
      toast.error('Please enter resolution notes');
      return;
    }
    setProcessing(true);
    try {
      await axios.post(
        `${API}/admin/resolve-dispute?match_id=${matchId}&winner_id=${winnerId}&resolution=${encodeURIComponent(resolutionNote)}`,
        {},
        { withCredentials: true }
      );
      toast.success('Dispute resolved!');
      setSelectedItem(null);
      setResolutionNote('');
      loadAdminData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to resolve');
    } finally {
      setProcessing(false);
    }
  };

  const declareMatchDraw = async (matchId) => {
    const confirmed = await showConfirm(
      'Declare this match as a draw? Both players will be refunded.',
      'Declare Draw'
    );
    if (!confirmed) return;
    
    setProcessing(true);
    try {
      await axios.post(`${API}/admin/matches/${matchId}/draw`, {}, { withCredentials: true });
      toast.success('Match declared as draw - both refunded');
      loadAdminData();
      loadMatches(matchStatusFilter);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed');
    } finally {
      setProcessing(false);
    }
  };

  const cancelMatch = async (matchId) => {
    const confirmed = await showConfirm(
      'Cancel this match and refund both players?',
      'Cancel Match'
    );
    if (!confirmed) return;
    
    setProcessing(true);
    try {
      await axios.post(`${API}/admin/matches/${matchId}/cancel`, {}, { withCredentials: true });
      toast.success('Match cancelled and refunded');
      loadMatches(matchStatusFilter);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed');
    } finally {
      setProcessing(false);
    }
  };

  const approveWithdrawal = async (withdrawalId) => {
    setProcessing(true);
    try {
      await axios.post(`${API}/admin/withdrawals/${withdrawalId}/approve`, {}, { withCredentials: true });
      toast.success('Withdrawal approved');
      loadAdminData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed');
    } finally {
      setProcessing(false);
    }
  };

  const rejectWithdrawal = async (withdrawalId) => {
    const reason = await showPrompt(
      'Rejection reason:',
      'Reject Withdrawal',
      'Rejected by admin'
    );
    if (!reason) return;
    
    setProcessing(true);
    try {
      await axios.post(`${API}/admin/withdrawals/${withdrawalId}/reject?reason=${encodeURIComponent(reason)}`, {}, { withCredentials: true });
      toast.success('Withdrawal rejected - amount refunded');
      loadAdminData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed');
    } finally {
      setProcessing(false);
    }
  };

  const flagWithdrawal = async (withdrawalId) => {
    const reason = await showPrompt(
      'Flag reason:',
      'Flag Withdrawal'
    );
    if (!reason) return;
    
    try {
      await axios.post(`${API}/admin/withdrawals/${withdrawalId}/flag?reason=${encodeURIComponent(reason)}`, {}, { withCredentials: true });
      toast.success('Withdrawal flagged');
      loadAdminData();
    } catch (error) {
      toast.error('Failed');
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 text-[#00F0FF] animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  const pendingWithdrawals = withdrawals.filter(w => w.status === 'pending');

  return (
    <DashboardLayout>
      <AlertDialog />
      <ConfirmDialog />
      <PromptDialog />
      
      <div className="max-w-7xl mx-auto px-4" data-testid="admin-panel">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#FF2E2E] to-[#FF6B35] flex items-center justify-center">
              <Shield className="w-7 h-7 text-white" />
            </div>
            <div>
              <h1 className="font-heading text-2xl sm:text-3xl font-bold">Admin Control Center</h1>
              <p className="text-white/50 text-sm">Manage your platform</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button onClick={() => navigate('/admin/settings')} variant="outline" className="btn-secondary">
              <Settings className="w-4 h-4 mr-2" /> Settings
            </Button>
            <Button onClick={loadAdminData} variant="outline" className="btn-secondary">
              <RefreshCw className="w-4 h-4 mr-2" /> Refresh
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-4">
            <StatCard icon={Users} label="Total Users" value={stats.total_users} color="#00F0FF" />
            <StatCard icon={Gamepad2} label="Total Matches" value={stats.total_matches} color="#7000FF" />
            <StatCard icon={DollarSign} label="Platform Fees" value={`$${(stats.total_platform_fees || 0).toFixed(2)}`} color="#CCFF00" />
            <StatCard icon={Wallet} label="Pending Withdrawals" value={stats.pending_withdrawals || 0} color="#FFB800" />
            <StatCard icon={Coins} label="User Liability (Cash)" value={`$${(stats.user_liability_cash || 0).toFixed(2)}`} color="#00FF94" />
            <StatCard icon={Target} label="User Liability (Credits)" value={stats.user_liability_credits?.toLocaleString() || 0} color="#FF2E2E" />
          </div>
        )}

        {/* New Users & Depositors Analytics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
          {/* New Users Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            onClick={loadRecentUsers}
            className="glass-card rounded-xl p-5 cursor-pointer hover:border-[#00F0FF]/50 transition-all group"
            data-testid="new-users-card"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#00F0FF]/20 flex items-center justify-center">
                  <UserPlus className="w-5 h-5 text-[#00F0FF]" />
                </div>
                <div>
                  <h3 className="font-semibold text-white">New Users</h3>
                  <p className="text-xs text-white/40">Click for details</p>
                </div>
              </div>
              <ChevronRight className="w-5 h-5 text-white/30 group-hover:text-[#00F0FF] transition-colors" />
            </div>
            {userAnalytics?.new_users ? (
              <div className="grid grid-cols-3 gap-3">
                <div className="p-3 rounded-lg bg-white/5 text-center">
                  <p className="text-white/50 text-xs mb-1">24h</p>
                  <p className="font-mono text-lg font-bold text-[#00F0FF]">{userAnalytics.new_users.last_24h}</p>
                </div>
                <div className="p-3 rounded-lg bg-white/5 text-center">
                  <p className="text-white/50 text-xs mb-1">7d</p>
                  <p className="font-mono text-lg font-bold text-[#00FF94]">{userAnalytics.new_users.last_7d}</p>
                </div>
                <div className="p-3 rounded-lg bg-white/5 text-center">
                  <p className="text-white/50 text-xs mb-1">30d</p>
                  <p className="font-mono text-lg font-bold text-[#CCFF00]">{userAnalytics.new_users.last_30d}</p>
                </div>
              </div>
            ) : (
              <div className="text-center py-4 text-white/40">Loading...</div>
            )}
          </motion.div>

          {/* New Depositors Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            onClick={loadRecentDepositors}
            className="glass-card rounded-xl p-5 cursor-pointer hover:border-[#CCFF00]/50 transition-all group"
            data-testid="new-depositors-card"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#CCFF00]/20 flex items-center justify-center">
                  <CreditCard className="w-5 h-5 text-[#CCFF00]" />
                </div>
                <div>
                  <h3 className="font-semibold text-white">New Depositors</h3>
                  <p className="text-xs text-white/40">Click for details</p>
                </div>
              </div>
              <ChevronRight className="w-5 h-5 text-white/30 group-hover:text-[#CCFF00] transition-colors" />
            </div>
            {depositorAnalytics?.new_depositors ? (
              <div className="grid grid-cols-3 gap-3">
                <div className="p-3 rounded-lg bg-white/5 text-center">
                  <p className="text-white/50 text-xs mb-1">24h</p>
                  <p className="font-mono text-lg font-bold text-[#00F0FF]">{depositorAnalytics.new_depositors.last_24h}</p>
                </div>
                <div className="p-3 rounded-lg bg-white/5 text-center">
                  <p className="text-white/50 text-xs mb-1">7d</p>
                  <p className="font-mono text-lg font-bold text-[#00FF94]">{depositorAnalytics.new_depositors.last_7d}</p>
                </div>
                <div className="p-3 rounded-lg bg-white/5 text-center">
                  <p className="text-white/50 text-xs mb-1">30d</p>
                  <p className="font-mono text-lg font-bold text-[#CCFF00]">{depositorAnalytics.new_depositors.last_30d}</p>
                </div>
              </div>
            ) : (
              <div className="text-center py-4 text-white/40">Loading...</div>
            )}
          </motion.div>
        </div>

        {/* New Users Modal */}
        {showNewUsersModal && (
          <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowNewUsersModal(false)}>
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-[#0A0A0B] border border-white/10 rounded-2xl w-full max-w-2xl max-h-[80vh] overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-5 border-b border-white/10 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <UserPlus className="w-6 h-6 text-[#00F0FF]" />
                  <h2 className="font-heading font-bold text-xl">Recent New Users</h2>
                </div>
                <Button variant="ghost" size="sm" onClick={() => setShowNewUsersModal(false)}>
                  <X className="w-5 h-5" />
                </Button>
              </div>
              <div className="p-5 overflow-y-auto max-h-[60vh]">
                {loadingModalData ? (
                  <div className="text-center py-8">
                    <Loader2 className="w-8 h-8 animate-spin text-[#00F0FF] mx-auto" />
                  </div>
                ) : recentUsers.length === 0 ? (
                  <div className="text-center py-8 text-white/50">No recent users found</div>
                ) : (
                  <div className="space-y-3">
                    {recentUsers.map((u, idx) => (
                      <div key={u.user_id || idx} className="p-4 rounded-xl bg-white/5 hover:bg-white/10 transition-colors">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-[#7000FF]/30 flex items-center justify-center text-white font-bold">
                              {u.name?.charAt(0) || 'U'}
                            </div>
                            <div>
                              <p className="font-medium">{u.name || 'Unknown'}</p>
                              <p className="text-sm text-white/50">{u.email || 'No email'}</p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-xs text-white/40">Joined</p>
                            <p className="text-sm text-white/70">{new Date(u.created_at).toLocaleDateString()}</p>
                            <p className="text-xs text-white/40">{new Date(u.created_at).toLocaleTimeString()}</p>
                          </div>
                        </div>
                        <div className="mt-2 flex items-center gap-2">
                          <span className={`text-xs px-2 py-0.5 rounded ${u.auth_provider === 'google' ? 'bg-[#4285F4]/20 text-[#4285F4]' : 'bg-white/10 text-white/60'}`}>
                            {u.auth_provider === 'google' ? 'Google' : 'Email'}
                          </span>
                          {u.is_verified && (
                            <span className="text-xs px-2 py-0.5 rounded bg-[#00FF94]/20 text-[#00FF94]">Verified</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </motion.div>
          </div>
        )}

        {/* New Depositors Modal */}
        {showDepositorsModal && (
          <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowDepositorsModal(false)}>
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-[#0A0A0B] border border-white/10 rounded-2xl w-full max-w-2xl max-h-[80vh] overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-5 border-b border-white/10 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <CreditCard className="w-6 h-6 text-[#CCFF00]" />
                  <h2 className="font-heading font-bold text-xl">Recent New Depositors</h2>
                </div>
                <Button variant="ghost" size="sm" onClick={() => setShowDepositorsModal(false)}>
                  <X className="w-5 h-5" />
                </Button>
              </div>
              <div className="p-5 overflow-y-auto max-h-[60vh]">
                {loadingModalData ? (
                  <div className="text-center py-8">
                    <Loader2 className="w-8 h-8 animate-spin text-[#CCFF00] mx-auto" />
                  </div>
                ) : recentDepositors.length === 0 ? (
                  <div className="text-center py-8 text-white/50">No recent depositors found</div>
                ) : (
                  <div className="space-y-3">
                    {recentDepositors.map((d, idx) => (
                      <div key={d.user_id || idx} className="p-4 rounded-xl bg-white/5 hover:bg-white/10 transition-colors">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-[#CCFF00]/30 flex items-center justify-center text-black font-bold">
                              {d.name?.charAt(0) || 'U'}
                            </div>
                            <div>
                              <p className="font-medium">{d.name || 'Unknown'}</p>
                              <p className="text-sm text-white/50">{d.email || 'No email'}</p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="font-mono text-lg font-bold text-[#CCFF00]">${d.first_deposit_amount?.toFixed(2) || '0.00'}</p>
                            <p className="text-xs text-white/40">{new Date(d.first_deposit_at).toLocaleDateString()}</p>
                          </div>
                        </div>
                        <div className="mt-2 flex items-center gap-2">
                          <span className={`text-xs px-2 py-0.5 rounded ${d.payment_method === 'venmo' ? 'bg-[#008CFF]/20 text-[#008CFF]' : 'bg-[#00D632]/20 text-[#00D632]'}`}>
                            {d.payment_method === 'venmo' ? 'Venmo' : 'Cash App'}
                          </span>
                          <span className="text-xs text-white/40">First deposit</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </motion.div>
          </div>
        )}

        {/* Search */}
        <div className="glass-card rounded-xl p-4 mb-6">
          <div className="flex gap-3">
            <Input
              placeholder="Search matches, users, or IDs..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && searchMatches()}
              className="input-dark flex-1"
              data-testid="admin-search-input"
            />
            <Button onClick={searchMatches} className="btn-primary" data-testid="admin-search-btn">
              <Search className="w-4 h-4 mr-2" /> Search
            </Button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          {[
            { id: 'overview', label: 'Overview', icon: TrendingUp },
            { id: 'announcements', label: 'Announcements', icon: Megaphone },
            { id: 'compliance', label: 'Compliance', icon: Shield },
            { id: 'financials', label: 'Financials', icon: DollarSign },
            { id: 'deposits', label: 'Deposits', icon: CreditCard, count: pendingDepositsCount },
            { id: 'disputes', label: 'Disputes', icon: AlertTriangle, count: disputes.length },
            { id: 'matches', label: 'Matches', icon: Gamepad2 },
            { id: 'users', label: 'Users', icon: Users },
            { id: 'withdrawals', label: 'Withdrawals', icon: Wallet, count: pendingWithdrawals.length },
          ].map(tab => (
            <Button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              variant={activeTab === tab.id ? 'default' : 'outline'}
              className={activeTab === tab.id ? 'btn-primary' : 'btn-secondary'}
              data-testid={`admin-tab-${tab.id}`}
            >
              <tab.icon className="w-4 h-4 mr-2" />
              {tab.label}
              {tab.count > 0 && (
                <span className="ml-2 px-2 py-0.5 bg-[#FF2E2E] text-white rounded-full text-xs">{tab.count}</span>
              )}
            </Button>
          ))}
        </div>

        {/* Content */}
        <div className="space-y-4">
          {/* Overview */}
          {activeTab === 'overview' && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="grid md:grid-cols-2 gap-6">
              <div className="glass-card rounded-xl p-6">
                <h3 className="font-semibold mb-4 flex items-center gap-2">
                  <Clock className="w-5 h-5 text-[#00F0FF]" /> Match Status
                </h3>
                <div className="space-y-3 text-sm">
                  <p className="text-white/70">• {stats?.active_matches || 0} active matches</p>
                  <p className="text-white/70">• {stats?.reserved_matches || 0} reserved (awaiting confirmation)</p>
                  <p className="text-white/70">• {stats?.completed_matches || 0} completed matches</p>
                  <p className="text-white/70">• {stats?.disputed_matches || 0} pending disputes</p>
                  <p className="text-white/70">• {stats?.frozen_matches || 0} frozen matches</p>
                </div>
              </div>
              <div className="glass-card rounded-xl p-6">
                <h3 className="font-semibold mb-4 flex items-center gap-2">
                  <Trophy className="w-5 h-5 text-[#CCFF00]" /> Platform Health
                </h3>
                <div className="space-y-3 text-sm">
                  <p className="text-[#00FF94]">✓ Database connected</p>
                  <p className="text-[#00FF94]">✓ Payments active</p>
                  <p className="text-[#00FF94]">✓ Auth system online</p>
                  <p className="text-[#FFB800]">• {pendingWithdrawals.length} pending withdrawal(s)</p>
                </div>
              </div>
            </motion.div>
          )}

          {/* Compliance Panel */}
          {activeTab === 'compliance' && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
              <CompliancePanel />
            </motion.div>
          )}

          {/* Announcements Panel */}
          {activeTab === 'announcements' && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
              <AdminAnnouncements />
            </motion.div>
          )}

          {/* Financial Dashboard */}
          {activeTab === 'financials' && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
              {financials ? (
                <>
                  {/* Total Revenue Cards */}
                  <div className="glass-card rounded-xl p-6">
                    <h3 className="font-heading font-semibold text-lg mb-4 flex items-center gap-2">
                      <DollarSign className="w-5 h-5 text-[#CCFF00]" />
                      Total Platform Revenue
                    </h3>
                    <div className="grid grid-cols-4 gap-4">
                      <div className="p-4 rounded-xl bg-white/5 text-center">
                        <p className="text-white/50 text-xs uppercase mb-1">Today</p>
                        <p className="font-mono text-2xl font-bold text-[#CCFF00]">${financials.revenue?.today?.toFixed(2) || '0.00'}</p>
                      </div>
                      <div className="p-4 rounded-xl bg-white/5 text-center">
                        <p className="text-white/50 text-xs uppercase mb-1">This Week</p>
                        <p className="font-mono text-2xl font-bold text-[#00F0FF]">${financials.revenue?.this_week?.toFixed(2) || '0.00'}</p>
                      </div>
                      <div className="p-4 rounded-xl bg-white/5 text-center">
                        <p className="text-white/50 text-xs uppercase mb-1">This Month</p>
                        <p className="font-mono text-2xl font-bold text-[#FFB800]">${financials.revenue?.this_month?.toFixed(2) || '0.00'}</p>
                      </div>
                      <div className="p-4 rounded-xl bg-white/5 text-center">
                        <p className="text-white/50 text-xs uppercase mb-1">All Time</p>
                        <p className="font-mono text-2xl font-bold text-[#00FF94]">${financials.revenue?.all_time?.toFixed(2) || '0.00'}</p>
                      </div>
                    </div>
                  </div>

                  {/* Revenue Breakdown */}
                  <div className="grid md:grid-cols-2 gap-6">
                    {/* Winnings Fees (3%) */}
                    <div className="glass-card rounded-xl p-6">
                      <h3 className="font-semibold mb-4 flex items-center gap-2">
                        <Trophy className="w-5 h-5 text-[#CCFF00]" />
                        Revenue from Winnings Fees ({financials.fee_config?.winnings_fee_percent || 3}%)
                      </h3>
                      <div className="space-y-3">
                        <div className="flex justify-between items-center p-3 rounded-lg bg-[#CCFF00]/10">
                          <span className="text-white/70">Today</span>
                          <span className="font-mono font-bold text-[#CCFF00]">${financials.revenue_breakdown?.winnings_fees?.today?.toFixed(2) || '0.00'}</span>
                        </div>
                        <div className="flex justify-between items-center p-3 rounded-lg bg-white/5">
                          <span className="text-white/70">This Week</span>
                          <span className="font-mono font-bold">${financials.revenue_breakdown?.winnings_fees?.this_week?.toFixed(2) || '0.00'}</span>
                        </div>
                        <div className="flex justify-between items-center p-3 rounded-lg bg-white/5">
                          <span className="text-white/70">All Time</span>
                          <span className="font-mono font-bold text-[#00FF94]">${financials.revenue_breakdown?.winnings_fees?.all_time?.toFixed(2) || '0.00'}</span>
                        </div>
                      </div>
                    </div>

                    {/* Withdrawal Fees (1.5%) */}
                    <div className="glass-card rounded-xl p-6">
                      <h3 className="font-semibold mb-4 flex items-center gap-2">
                        <Wallet className="w-5 h-5 text-[#FFB800]" />
                        Revenue from Withdrawal Fees ({financials.fee_config?.withdrawal_fee_percent || 1.5}%, max ${financials.fee_config?.withdrawal_fee_cap || 10})
                      </h3>
                      <div className="space-y-3">
                        <div className="flex justify-between items-center p-3 rounded-lg bg-[#FFB800]/10">
                          <span className="text-white/70">Today</span>
                          <span className="font-mono font-bold text-[#FFB800]">${financials.revenue_breakdown?.withdrawal_fees?.today?.toFixed(2) || '0.00'}</span>
                        </div>
                        <div className="flex justify-between items-center p-3 rounded-lg bg-white/5">
                          <span className="text-white/70">This Week</span>
                          <span className="font-mono font-bold">${financials.revenue_breakdown?.withdrawal_fees?.this_week?.toFixed(2) || '0.00'}</span>
                        </div>
                        <div className="flex justify-between items-center p-3 rounded-lg bg-white/5">
                          <span className="text-white/70">All Time</span>
                          <span className="font-mono font-bold text-[#00FF94]">${financials.revenue_breakdown?.withdrawal_fees?.all_time?.toFixed(2) || '0.00'}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Payouts & Profit */}
                  <div className="grid md:grid-cols-3 gap-4">
                    {/* Total Payouts */}
                    <div className="glass-card rounded-xl p-5">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-10 h-10 rounded-lg bg-[#FF2E2E]/20 flex items-center justify-center">
                          <TrendingUp className="w-5 h-5 text-[#FF2E2E]" />
                        </div>
                        <div>
                          <p className="text-white/50 text-xs">Total Payouts Sent</p>
                          <p className="font-mono text-xl font-bold text-[#FF2E2E]">${financials.payouts?.all_time?.toFixed(2) || '0.00'}</p>
                        </div>
                      </div>
                      <p className="text-white/40 text-xs">Today: ${financials.payouts?.today?.toFixed(2) || '0.00'}</p>
                    </div>

                    {/* Net Profit */}
                    <div className="glass-card rounded-xl p-5">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-10 h-10 rounded-lg bg-[#00FF94]/20 flex items-center justify-center">
                          <TrendingUp className="w-5 h-5 text-[#00FF94]" />
                        </div>
                        <div>
                          <p className="text-white/50 text-xs">Net Platform Profit</p>
                          <p className="font-mono text-xl font-bold text-[#00FF94]">${financials.net_profit?.toFixed(2) || '0.00'}</p>
                        </div>
                      </div>
                      <p className="text-white/40 text-xs">Revenue - Expenses</p>
                    </div>

                    {/* Escrow Balance */}
                    <div className="glass-card rounded-xl p-5">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-10 h-10 rounded-lg bg-[#7000FF]/20 flex items-center justify-center">
                          <Shield className="w-5 h-5 text-[#7000FF]" />
                        </div>
                        <div>
                          <p className="text-white/50 text-xs">Funds in Active Match Escrow</p>
                          <p className="font-mono text-xl font-bold text-[#7000FF]">${financials.escrow_liability?.amount?.toFixed(2) || '0.00'}</p>
                        </div>
                      </div>
                      <p className="text-white/40 text-xs">
                        Locked in {financials.escrow_liability?.match_count || 0} active match(es)
                      </p>
                    </div>
                  </div>

                  {/* User Liability & Withdrawals */}
                  <div className="grid md:grid-cols-2 gap-6">
                    {/* User Balances (Liability) */}
                    <div className="glass-card rounded-xl p-6">
                      <h3 className="font-semibold mb-4 flex items-center gap-2">
                        <Users className="w-5 h-5 text-[#00F0FF]" />
                        User Balances (Liability)
                      </h3>
                      <div className="space-y-3">
                        <div className="flex justify-between items-center p-3 rounded-lg bg-[#00F0FF]/10">
                          <span className="text-white/70">Cash Balances</span>
                          <span className="font-mono font-bold text-[#00F0FF]">${financials.user_liability?.cash?.toFixed(2) || '0.00'}</span>
                        </div>
                        <div className="flex justify-between items-center p-3 rounded-lg bg-white/5">
                          <span className="text-white/70">Credit Balances</span>
                          <span className="font-mono font-bold">{financials.user_liability?.credits?.toLocaleString() || 0} credits</span>
                        </div>
                        <div className="flex justify-between items-center p-3 rounded-lg bg-white/5">
                          <span className="text-white/70">Credits Value</span>
                          <span className="font-mono font-bold">${financials.user_liability?.credits_as_cash?.toFixed(2) || '0.00'}</span>
                        </div>
                        <div className="flex justify-between items-center p-3 rounded-lg bg-[#7000FF]/10 border border-[#7000FF]/30">
                          <span className="text-white/70">Active Match Escrow</span>
                          <span className="font-mono font-bold text-[#7000FF]">${financials.escrow_liability?.amount?.toFixed(2) || '0.00'}</span>
                        </div>
                        <div className="flex justify-between items-center p-3 rounded-lg bg-[#FF2E2E]/10 border border-[#FF2E2E]/30">
                          <span className="font-semibold">Total Platform Liability</span>
                          <span className="font-mono font-bold text-[#FF2E2E]">${financials.total_platform_liability?.toFixed(2) || financials.user_liability?.total?.toFixed(2) || '0.00'}</span>
                        </div>
                      </div>
                    </div>

                    {/* Withdrawal Statistics */}
                    <div className="glass-card rounded-xl p-6">
                      <h3 className="font-semibold mb-4 flex items-center gap-2">
                        <Wallet className="w-5 h-5 text-[#FFB800]" />
                        Withdrawal Statistics
                      </h3>
                      <div className="space-y-3">
                        <div className="flex justify-between items-center p-3 rounded-lg bg-[#FFB800]/10">
                          <span className="text-white/70">Pending Withdrawals</span>
                          <div className="text-right">
                            <span className="font-mono font-bold text-[#FFB800]">${financials.withdrawals?.pending?.amount?.toFixed(2) || '0.00'}</span>
                            <p className="text-white/40 text-xs">{financials.withdrawals?.pending?.count || 0} request(s)</p>
                          </div>
                        </div>
                        <div className="flex justify-between items-center p-3 rounded-lg bg-white/5">
                          <span className="text-white/70">Completed Withdrawals</span>
                          <div className="text-right">
                            <span className="font-mono font-bold text-[#00FF94]">${financials.withdrawals?.completed?.amount?.toFixed(2) || '0.00'}</span>
                            <p className="text-white/40 text-xs">{financials.withdrawals?.completed?.count || 0} completed</p>
                          </div>
                        </div>
                        {/* Payment Method Breakdown */}
                        <div className="pt-2 border-t border-white/10">
                          <p className="text-xs text-white/40 mb-2">By Payment Method</p>
                          <div className="grid grid-cols-2 gap-2">
                            <div className="p-2 rounded-lg bg-[#00D632]/10 text-center">
                              <p className="text-xs text-white/50">Cash App</p>
                              <p className="font-mono font-bold text-[#00D632]">${financials.withdrawals?.by_method?.cashapp?.amount?.toFixed(2) || '0.00'}</p>
                              <p className="text-xs text-white/40">{financials.withdrawals?.by_method?.cashapp?.count || 0}</p>
                            </div>
                            <div className="p-2 rounded-lg bg-[#3D95CE]/10 text-center">
                              <p className="text-xs text-white/50">Venmo</p>
                              <p className="font-mono font-bold text-[#3D95CE]">${financials.withdrawals?.by_method?.venmo?.amount?.toFixed(2) || '0.00'}</p>
                              <p className="text-xs text-white/40">{financials.withdrawals?.by_method?.venmo?.count || 0}</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Deposits & Match Stats */}
                  <div className="grid md:grid-cols-2 gap-6">
                    {/* Deposit Statistics */}
                    <div className="glass-card rounded-xl p-6">
                      <h3 className="font-semibold mb-4 flex items-center gap-2">
                        <CreditCard className="w-5 h-5 text-[#00FF94]" />
                        Deposit Statistics
                      </h3>
                      <div className="space-y-3">
                        <div className="flex justify-between items-center p-3 rounded-lg bg-[#FFB800]/10">
                          <span className="text-white/70">Pending Deposits</span>
                          <div className="text-right">
                            <span className="font-mono font-bold text-[#FFB800]">${financials.deposits?.pending?.amount?.toFixed(2) || '0.00'}</span>
                            <p className="text-white/40 text-xs">{financials.deposits?.pending?.count || 0} request(s)</p>
                          </div>
                        </div>
                        <div className="flex justify-between items-center p-3 rounded-lg bg-white/5">
                          <span className="text-white/70">Approved Deposits</span>
                          <div className="text-right">
                            <span className="font-mono font-bold text-[#00FF94]">${financials.deposits?.approved?.amount?.toFixed(2) || '0.00'}</span>
                            <p className="text-white/40 text-xs">{financials.deposits?.approved?.count || 0} approved</p>
                          </div>
                        </div>
                        {/* Payment Method Breakdown */}
                        <div className="pt-2 border-t border-white/10">
                          <p className="text-xs text-white/40 mb-2">By Payment Method</p>
                          <div className="grid grid-cols-2 gap-2">
                            <div className="p-2 rounded-lg bg-[#00D632]/10 text-center">
                              <p className="text-xs text-white/50">Cash App</p>
                              <p className="font-mono font-bold text-[#00D632]">${financials.deposits?.by_method?.cashapp?.amount?.toFixed(2) || '0.00'}</p>
                              <p className="text-xs text-white/40">{financials.deposits?.by_method?.cashapp?.count || 0}</p>
                            </div>
                            <div className="p-2 rounded-lg bg-[#3D95CE]/10 text-center">
                              <p className="text-xs text-white/50">Venmo</p>
                              <p className="font-mono font-bold text-[#3D95CE]">${financials.deposits?.by_method?.venmo?.amount?.toFixed(2) || '0.00'}</p>
                              <p className="text-xs text-white/40">{financials.deposits?.by_method?.venmo?.count || 0}</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Match Statistics */}
                    <div className="glass-card rounded-xl p-6">
                      <h3 className="font-semibold mb-4 flex items-center gap-2">
                        <Gamepad2 className="w-5 h-5 text-[#7000FF]" />
                        Match Statistics
                      </h3>
                      <div className="space-y-3">
                        <div className="flex justify-between items-center p-3 rounded-lg bg-white/5">
                          <span className="text-white/70">Total Matches</span>
                          <span className="font-mono font-bold">{financials.matches?.total || 0}</span>
                        </div>
                        <div className="flex justify-between items-center p-3 rounded-lg bg-white/5">
                          <span className="text-white/70">Completed Matches</span>
                          <span className="font-mono font-bold text-[#00FF94]">{financials.matches?.completed || 0}</span>
                        </div>
                        <div className="flex justify-between items-center p-3 rounded-lg bg-white/5">
                          <span className="text-white/70">Today's Matches</span>
                          <span className="font-mono font-bold text-[#00F0FF]">{financials.matches?.today || 0}</span>
                        </div>
                        <div className="flex justify-between items-center p-3 rounded-lg bg-white/5">
                          <span className="text-white/70">This Week's Matches</span>
                          <span className="font-mono font-bold text-[#FFB800]">{financials.matches?.this_week || 0}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Fee Configuration Summary */}
                  <div className="glass-card rounded-xl p-6">
                    <h3 className="font-semibold mb-4 flex items-center gap-2">
                      <Settings className="w-5 h-5 text-white/50" />
                      Current Fee Configuration
                    </h3>
                    <div className="grid md:grid-cols-5 gap-4">
                      <div className="p-3 rounded-lg bg-white/5 text-center">
                        <p className="text-white/50 text-xs mb-1">Winnings Fee</p>
                        <p className="font-mono font-bold text-[#CCFF00]">{financials.fee_config?.winnings_fee_percent || 3}%</p>
                      </div>
                      <div className="p-3 rounded-lg bg-white/5 text-center">
                        <p className="text-white/50 text-xs mb-1">Withdrawal Fee</p>
                        <p className="font-mono font-bold text-[#FFB800]">{financials.fee_config?.withdrawal_fee_percent || 1.5}%</p>
                      </div>
                      <div className="p-3 rounded-lg bg-white/5 text-center">
                        <p className="text-white/50 text-xs mb-1">Withdrawal Cap</p>
                        <p className="font-mono font-bold">${financials.fee_config?.withdrawal_fee_cap || 10}</p>
                      </div>
                      <div className="p-3 rounded-lg bg-white/5 text-center">
                        <p className="text-white/50 text-xs mb-1">Min Withdrawal</p>
                        <p className="font-mono font-bold">${financials.fee_config?.min_withdrawal || 20}</p>
                      </div>
                      <div className="p-3 rounded-lg bg-white/5 text-center">
                        <p className="text-white/50 text-xs mb-1">Max Withdrawal</p>
                        <p className="font-mono font-bold">${financials.fee_config?.max_withdrawal || 500}</p>
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                <div className="glass-card rounded-xl p-12 text-center">
                  <Loader2 className="w-8 h-8 text-[#00F0FF] animate-spin mx-auto mb-4" />
                  <p className="text-white/50">Loading financial data...</p>
                </div>
              )}
            </motion.div>
          )}

          {/* Deposits */}
          {activeTab === 'deposits' && (
            <AdminDeposits onRefresh={loadAdminData} />
          )}

          {/* Disputes */}
          {activeTab === 'disputes' && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
              {disputes.length === 0 ? (
                <EmptyState icon={CheckCircle} message="No pending disputes" />
              ) : (
                disputes.map(dispute => (
                  <div key={dispute.dispute_id} className="glass-card rounded-xl p-6 mb-4" data-testid={`dispute-${dispute.dispute_id}`}>
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <p className="text-white/50 text-sm">Match: {dispute.match_id}</p>
                        <p className="text-[#FF2E2E] mt-1">{dispute.reason}</p>
                      </div>
                      <Button 
                        size="sm" 
                        onClick={() => setSelectedItem(selectedItem === dispute.dispute_id ? null : dispute)}
                        className="btn-secondary"
                      >
                        <Eye className="w-4 h-4 mr-1" /> {selectedItem?.dispute_id === dispute.dispute_id ? 'Close' : 'Review'}
                      </Button>
                    </div>
                    
                    {selectedItem?.dispute_id === dispute.dispute_id && (
                      <DisputeResolver
                        dispute={dispute}
                        onResolve={resolveDispute}
                        onDraw={() => declareMatchDraw(dispute.match_id)}
                        resolutionNote={resolutionNote}
                        setResolutionNote={setResolutionNote}
                        processing={processing}
                      />
                    )}
                  </div>
                ))
              )}
            </motion.div>
          )}

          {/* Matches */}
          {activeTab === 'matches' && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
              {/* Filter */}
              <div className="flex gap-2 mb-4 flex-wrap">
                {['', 'open', 'reserved', 'in_progress', 'awaiting_proof', 'disputed', 'frozen', 'completed', 'cancelled'].map(status => (
                  <Button
                    key={status}
                    size="sm"
                    variant={matchStatusFilter === status ? 'default' : 'outline'}
                    className={matchStatusFilter === status ? 'btn-primary' : 'btn-secondary'}
                    onClick={() => setMatchStatusFilter(status)}
                  >
                    {status || 'All'}
                  </Button>
                ))}
              </div>
              
              {matches.length === 0 ? (
                <EmptyState icon={Gamepad2} message="No matches found" />
              ) : (
                <div className="glass-card rounded-xl overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-white/5">
                        <tr>
                          <th className="text-left p-3 text-white/50 font-normal">Match</th>
                          <th className="text-left p-3 text-white/50 font-normal">Players</th>
                          <th className="text-left p-3 text-white/50 font-normal">Entry</th>
                          <th className="text-left p-3 text-white/50 font-normal">Status</th>
                          <th className="text-right p-3 text-white/50 font-normal">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {matches.map(match => (
                          <tr key={match.match_id} className="border-t border-white/5 hover:bg-white/5" data-testid={`admin-match-${match.match_id}`}>
                            <td className="p-3">
                              <p className="font-medium">{match.game_name}</p>
                              <p className="text-white/40 text-xs">{match.match_id}</p>
                            </td>
                            <td className="p-3">
                              <p>{match.creator_name}</p>
                              <p className="text-white/50">vs {match.opponent_name || 'Waiting'}</p>
                            </td>
                            <td className="p-3 font-mono text-[#CCFF00]">${match.wager_amount?.toFixed(2)}</td>
                            <td className="p-3">
                              <StatusBadge status={match.status} />
                            </td>
                            <td className="p-3 text-right space-x-1">
                              <Button size="sm" variant="ghost" onClick={() => navigate(`/match/${match.match_id}`)}>
                                <Eye className="w-3 h-3" />
                              </Button>
                              {!['completed', 'cancelled'].includes(match.status) && (
                                <>
                                  <Button size="sm" variant="ghost" className="text-[#FF2E2E]" onClick={() => cancelMatch(match.match_id)}>
                                    <XCircle className="w-3 h-3" />
                                  </Button>
                                  {match.opponent_id && (
                                    <Button size="sm" variant="ghost" className="text-[#FFB800]" onClick={() => declareMatchDraw(match.match_id)}>
                                      Draw
                                    </Button>
                                  )}
                                </>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </motion.div>
          )}

          {/* Users */}
          {activeTab === 'users' && (
            <AdminUsers users={users} loadAdminData={loadAdminData} searchQuery={searchQuery} />
          )}

          {/* Withdrawals */}
          {activeTab === 'withdrawals' && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
              {withdrawals.length === 0 ? (
                <div className="glass-card rounded-xl p-12 text-center">
                  <Wallet className="w-12 h-12 text-white/20 mx-auto mb-4" />
                  <p className="text-white/50">No withdrawal requests</p>
                  {stats?.pending_withdrawals > 0 && (
                    <p className="text-[#FFB800] text-sm mt-2">
                      Stats show {stats.pending_withdrawals} pending withdrawal(s), but failed to load details. Try refreshing.
                    </p>
                  )}
                </div>
              ) : (
                <div className="glass-card rounded-xl overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-white/5">
                        <tr>
                          <th className="text-left p-3 text-white/50 font-normal">User</th>
                          <th className="text-left p-3 text-white/50 font-normal">Amount</th>
                          <th className="text-left p-3 text-white/50 font-normal">Balance</th>
                          <th className="text-left p-3 text-white/50 font-normal">Status</th>
                          <th className="text-left p-3 text-white/50 font-normal">Date</th>
                          <th className="text-right p-3 text-white/50 font-normal">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {withdrawals.map(w => (
                          <tr key={w.withdrawal_id} className="border-t border-white/5 hover:bg-white/5" data-testid={`withdrawal-${w.withdrawal_id}`}>
                            <td className="p-3">
                              <p className="font-medium">{w.user_name || 'Unknown'}</p>
                              <p className="text-white/40 text-xs">{w.user_email}</p>
                            </td>
                            <td className="p-3 font-mono text-[#CCFF00]">${w.amount?.toFixed(2)}</td>
                            <td className="p-3 font-mono text-white/70">${(w.user_balance || 0).toFixed(2)}</td>
                            <td className="p-3">
                              <StatusBadge status={w.status} />
                              {w.flagged && <span className="ml-2 text-xs text-[#FF2E2E]">FLAGGED</span>}
                            </td>
                            <td className="p-3 text-white/50 text-xs">
                              {new Date(w.created_at).toLocaleDateString()}
                            </td>
                            <td className="p-3 text-right space-x-1">
                              {w.status === 'pending' && (
                                <>
                                  <Button size="sm" variant="ghost" className="text-[#00FF94]" onClick={() => approveWithdrawal(w.withdrawal_id)} disabled={processing}>
                                    <CheckCircle className="w-3 h-3" />
                                  </Button>
                                  <Button size="sm" variant="ghost" className="text-[#FF2E2E]" onClick={() => rejectWithdrawal(w.withdrawal_id)} disabled={processing}>
                                    <XCircle className="w-3 h-3" />
                                  </Button>
                                  <Button size="sm" variant="ghost" className="text-[#FFB800]" onClick={() => flagWithdrawal(w.withdrawal_id)}>
                                    <Flag className="w-3 h-3" />
                                  </Button>
                                </>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </motion.div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

// Helper Components
const StatCard = ({ icon: Icon, label, value, color }) => (
  <motion.div 
    initial={{ opacity: 0, y: 20 }} 
    animate={{ opacity: 1, y: 0 }}
    className="glass-card rounded-xl p-4"
  >
    <Icon className="w-5 h-5 mb-2" style={{ color }} />
    <p className="text-white/50 text-xs">{label}</p>
    <p className="font-mono text-xl font-bold" style={{ color }}>{value}</p>
  </motion.div>
);

const EmptyState = ({ icon: Icon, message }) => (
  <div className="glass-card rounded-xl p-12 text-center">
    <Icon className="w-12 h-12 text-white/20 mx-auto mb-4" />
    <p className="text-white/50">{message}</p>
  </div>
);

const StatusBadge = ({ status }) => {
  const colors = {
    open: 'bg-[#00F0FF]/20 text-[#00F0FF]',
    reserved: 'bg-[#7000FF]/20 text-[#7000FF]',
    in_progress: 'bg-[#FFB800]/20 text-[#FFB800]',
    awaiting_proof: 'bg-[#FFB800]/20 text-[#FFB800]',
    disputed: 'bg-[#FF2E2E]/20 text-[#FF2E2E]',
    frozen: 'bg-[#FF2E2E]/20 text-[#FF2E2E]',
    completed: 'bg-[#00FF94]/20 text-[#00FF94]',
    cancelled: 'bg-white/10 text-white/50',
    pending: 'bg-[#FFB800]/20 text-[#FFB800]',
    approved: 'bg-[#00FF94]/20 text-[#00FF94]',
    rejected: 'bg-[#FF2E2E]/20 text-[#FF2E2E]',
  };
  return (
    <span className={`text-xs px-2 py-1 rounded ${colors[status] || 'bg-white/10 text-white/50'}`}>
      {status?.replace('_', ' ')}
    </span>
  );
};

const DisputeResolver = ({ dispute, onResolve, onDraw, resolutionNote, setResolutionNote, processing }) => {
  const [match, setMatch] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchMatch = async () => {
      try {
        const res = await axios.get(`${API}/admin/match/${dispute.match_id}`, { withCredentials: true });
        setMatch(res.data);
      } catch (e) {
        console.error('Failed to load match');
      } finally {
        setLoading(false);
      }
    };
    fetchMatch();
  }, [dispute.match_id]);

  if (loading) return <div className="h-20 bg-white/5 rounded animate-pulse" />;
  if (!match) return <p className="text-red-400">Failed to load match details</p>;

  return (
    <div className="border-t border-white/10 pt-4 mt-4 space-y-4">
      <div className="grid md:grid-cols-2 gap-4">
        <PlayerCard 
          label="Creator" 
          name={match.creator_name} 
          proof={match.creator_proof}
          claim={match.creator_claimed_win}
          aiValid={match.creator_proof_ai_valid}
          aiNotes={match.creator_proof_ai_notes}
          onAward={() => onResolve(match.match_id, match.creator_id)}
          disabled={processing || !resolutionNote.trim()}
        />
        <PlayerCard 
          label="Opponent" 
          name={match.opponent_name} 
          proof={match.opponent_proof}
          claim={match.opponent_claimed_win}
          aiValid={match.opponent_proof_ai_valid}
          aiNotes={match.opponent_proof_ai_notes}
          onAward={() => onResolve(match.match_id, match.opponent_id)}
          disabled={processing || !resolutionNote.trim()}
        />
      </div>
      <textarea
        placeholder="Resolution notes (required)..."
        value={resolutionNote}
        onChange={(e) => setResolutionNote(e.target.value)}
        className="w-full p-3 rounded-lg bg-white/5 border border-white/10 text-white placeholder:text-white/30 focus:border-[#00F0FF] focus:outline-none resize-none"
        rows={2}
      />
      <div className="flex gap-2">
        <Button variant="outline" className="btn-secondary flex-1" onClick={onDraw} disabled={processing}>
          Declare Draw (Refund Both)
        </Button>
      </div>
    </div>
  );
};

const PlayerCard = ({ label, name, proof, claim, aiValid, aiNotes, onAward, disabled }) => (
  <div className="p-4 rounded-lg bg-white/5">
    <p className="text-white/50 text-xs mb-1">{label}</p>
    <p className="font-semibold mb-2">{name}</p>
    {proof ? (
      <a href={proof} target="_blank" rel="noopener noreferrer" className="text-[#00F0FF] text-sm hover:underline block mb-1">View Proof</a>
    ) : (
      <p className="text-white/30 text-sm mb-1">No proof</p>
    )}
    <p className="text-sm">
      Claimed: <span className={claim ? 'text-[#00FF94]' : 'text-[#FF2E2E]'}>{claim ? 'Win' : 'Loss'}</span>
    </p>
    {aiValid !== null && aiValid !== undefined && (
      <p className={`text-xs mt-1 ${aiValid ? 'text-[#00FF94]' : 'text-[#FFB800]'}`}>
        AI: {aiValid ? '✓ Valid' : '⚠ Suspicious'}
      </p>
    )}
    {aiNotes && <p className="text-xs text-white/40 mt-1">{aiNotes}</p>}
    <Button size="sm" onClick={onAward} disabled={disabled} className="mt-3 bg-[#00FF94] text-black hover:bg-[#00FF94]/90">
      <CheckCircle className="w-4 h-4 mr-1" /> Award Win
    </Button>
  </div>
);

export default AdminPanel;
